import React, { useContext } from "react";
import {
	Avatar,
	Box,
	Button,
	Divider,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Toolbar,
	Typography,
	Tooltip,
	useMediaQuery,
} from "@mui/material";
import {
	Dashboard as DashboardIcon,
	PrecisionManufacturing as MachineIcon,
	People as PeopleIcon,
	Build as BuildIcon,
	Work as WorkIcon,
	Inventory as InventoryIcon,
	Logout as LogoutIcon,
	Assignment as RentingIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import authService from "../services/auth-service";
import Context from "../context";

const drawerWidth = 240;

const menuItemsAdmin = [
	{ text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
	{ text: "Machines", icon: <MachineIcon />, path: "/machines" },
	{ text: "Clients", icon: <PeopleIcon />, path: "/clients" },
	{ text: "Technicians", icon: <BuildIcon />, path: "/technicians" },
	{ text: "Backoffice Workers", icon: <WorkIcon />, path: "/backofficeWorkers" },
	{ text: "Items", icon: <InventoryIcon />, path: "/items" },
	{ text: "Rentings by User", icon: <RentingIcon />, path: "/rentings-by-user" },
];

const menuItemsBO = [
	{ text: "Machines", icon: <MachineIcon />, path: "/machines" },
	{ text: "Clients", icon: <PeopleIcon />, path: "/clients" },
	{ text: "Technicians", icon: <BuildIcon />, path: "/technicians" },
	{ text: "Rentings by User", icon: <RentingIcon />, path: "/rentings-by-user" },
];

export default function NavBar() {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { t } = useTranslation();
	const user = useContext(Context);

	const isMobile = useMediaQuery("(max-width:600px)"); // Media query for mobile view

	const logout = () => {
		authService.logout();
		navigate("/login");
	};

	const role = user?.roles[0];

	const menuItems = role === "ADMIN" ? menuItemsAdmin : menuItemsBO;

	// Hide Drawer if user is TECHNICIAN or on mobile
	if (role === "TECHNICIAN" || isMobile) {
		return null;
	}

	return (
		<Box sx={{ display: "flex" }}>
			<Drawer
				variant="permanent"
				sx={{
					width: drawerWidth,
					flexShrink: 0,
					[`& .MuiDrawer-paper`]: {
						width: drawerWidth,
						boxSizing: "border-box",
						borderRight: "1px solid #e0e0e0",
					},
				}}
			>
				{/* User Info */}
				<Toolbar />
				<Box sx={{ overflow: "auto", padding: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
						<Avatar
							alt={user?.name}
							src="/static/images/avatar/1.jpg" // Replace with user avatar if available
							sx={{ width: 56, height: 56, mr: 2 }}
						/>
						<Box>
							<Typography variant="h6" color="textPrimary">
								{user?.name}
							</Typography>
							<Typography variant="body2" color="textSecondary">
								{role === "ADMIN" ? "ADMINISTRADOR" : "BACK-OFFICE"}
							</Typography>
						</Box>
					</Box>
					<Divider />

					{/* Navigation Menu */}
					<List>
						{menuItems.map((item) => (
							<ListItem key={item.text} disablePadding>
								<ListItemButton
									selected={pathname === item.path}
									onClick={() => navigate(item.path)}
									sx={{
										borderRadius: 1,
										mb: 1,
										"&.Mui-selected": {
											backgroundColor: "primary.main",
											color: "#fff",
											"& .MuiListItemIcon-root": {
												color: "#fff",
											},
										},
										"&:hover": {
											backgroundColor: "action.hover",
										},
									}}
								>
									<ListItemIcon>
										<Tooltip title={item.text} placement="right">
											{item.icon}
										</Tooltip>
									</ListItemIcon>
									<ListItemText primary={t(`GENERIC.${item.text.toUpperCase()}`)} />
								</ListItemButton>
							</ListItem>
						))}
					</List>
					<Divider />

					{/* Logout Button */}
					<Box sx={{ mt: 2 }}>
						<Button
							variant="outlined"
							startIcon={<LogoutIcon />}
							fullWidth
							onClick={logout}
							sx={{
								textTransform: "none",
								color: "error.main",
								borderColor: "error.main",
								"&:hover": {
									backgroundColor: "error.light",
									borderColor: "error.main",
									color: "#fff",
								},
							}}
						>
							{t("AUTH.LOGOUT")}
						</Button>
					</Box>
				</Box>
			</Drawer>

			{/* Main Content Placeholder */}

		</Box>
	);
}
